import OnboardingReviewWorkspace from './components/OnboardingReviewWorkspace';
import React, { useState } from "react";
import { buildTalentDashboard } from "../../services/talent/TalentService";

import DepartmentLayout from "../../enterprise/layouts/DepartmentLayout";
import EnterpriseHeader from "../../enterprise/components/EnterpriseHeader";
import OperationsCoach from "../../enterprise/components/OperationsCoach";
import PipelineSummary from "./components/PipelineSummary.jsx";
import ReadyWorkforce from "./components/ReadyWorkforce.jsx";
import ReviewQueue from "./components/ReviewQueue.jsx";
import ApplicantTable from "./components/ApplicantTable.jsx";
import ApplicantRow from "./components/ApplicantRow.jsx";
import Applicant360Panel from "./components/Applicant360Panel.jsx";
import useApplicants from "../../hooks/useApplicants";


function assistButtonStyle(bg, color) {
  return {
    width: "100%",
    textAlign: "left",
    border: `1px solid ${bg}`,
    background: bg,
    color,
    borderRadius: 12,
    padding: "10px 12px",
    fontWeight: 950,
    cursor: "pointer",
  };
}

export default function TalentPipeline({
  profile,
  facilities = [],
  onInviteUser,
  onApproveRegistration,
  onReviewWorker,
  onViewWorker,
  onSendReminder,
  onNavigate,
}) {
const applicantContext = useApplicants();

const normalizePipelineRole = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const workerPipelineRoles = new Set([
  "worker",
  "nurse",
  "caregiver",
  "care_giver",
  "cna",
  "lvn",
  "lpn",
  "rn",
  "med_aide",
]);

const isWorkerPipelineRecord = (record) => {
  const roleValues = [
    record?.role,
    record?.platformRole,
    record?.primaryRole,
    record?.position,
    record?.workerType,
    record?.roleType,
    record?.accountType,
    record?.userType,
  ].map(normalizePipelineRole);

  return roleValues.some((role) =>
    workerPipelineRoles.has(role)
  );
};

const scopedApplicants = Array.isArray(applicantContext?.applicants)
  ? applicantContext.applicants
  : [];

const pipelineInvites = scopedApplicants.filter(
  (record) =>
    record?.pipelineStage === "invited" &&
    isWorkerPipelineRecord(record)
);

const pipelineUsers = scopedApplicants.filter(
  (record) =>
    record?.pipelineStage !== "invited" &&
    isWorkerPipelineRecord(record)
);

const pipelineFacilities =
  applicantContext?.facilities ?? facilities;

  const [pipelineSearch, setPipelineSearch] = useState("");
  const [pipelineRoleFilter, setPipelineRoleFilter] = useState("all");
  const [pipelineFacilityFilter, setPipelineFacilityFilter] = useState("all");
  const [pipelineStageFilter, setPipelineStageFilter] = useState("all");
  const [pipelineSourceFilter, setPipelineSourceFilter] = useState("all");

  // Applicant 360 panel
  const [selectedApplicant, setSelectedApplicant] = useState(null);

  const [openStages, setOpenStages] = useState({
    invited: true,
    registered: true,
    onboarding: true,
    review_queue: true,
    action_required: true,
  });

const {
  pipelineRows,
  counts,
  reviewQueueWorkers,
  approvedWorkers,
  filteredPipelineRows,
} = buildTalentDashboard({
  users: pipelineUsers,
  invites: pipelineInvites,
  filters: {
    search: pipelineSearch,
    role: pipelineRoleFilter,
    facility: pipelineFacilityFilter,
    source: pipelineSourceFilter,
    stage: pipelineStageFilter,
  },
});

console.table(
  pipelineRows.map((u) => ({
    email: u.email,
    role: u.role,
    platformRole: u.platformRole,
    primaryRole: u.primaryRole,
    position: u.position,
    userType: u.userType,
    facilityId: u.facilityId,
    stage: u.pipelineStage,
  }))
);

const stageDefinitions = [
  {
    key: "invited",
    label: "Invited",
    icon: "🟦",
    background: "#eff6ff",
    border: "#bfdbfe",
    color: "#1d4ed8",
  },
  {
    key: "registered",
    label: "Registered",
    icon: "🟨",
    background: "#fefce8",
    border: "#fde68a",
    color: "#92400e",
  },
  {
    key: "onboarding",
    label: "Onboarding",
    icon: "🟧",
    background: "#fff7ed",
    border: "#fdba74",
    color: "#9a3412",
  },
  {
    key: "review_queue",
    label: "Review Queue",
    icon: "🟥",
    background: "#fef2f2",
    border: "#fecaca",
    color: "#991b1b",
  },
  {
    key: "action_required",
    label: "Action Required",
    icon: "🟪",
    background: "#faf5ff",
    border: "#d8b4fe",
    color: "#7e22ce",
  },
];

const groupedPipelineRows = stageDefinitions
  .map((stage) => ({
    ...stage,
    workers: filteredPipelineRows.filter(
      (worker) => worker.pipelineStage === stage.key
    ),
  }))
  .filter(
    (stage) =>
      pipelineStageFilter === "all" ||
      pipelineStageFilter === stage.key
  );

function toggleStage(stageKey) {
  setOpenStages((prev) => ({
    ...prev,
    [stageKey]: !prev[stageKey],
  }));
}

return (
  <OnboardingReviewWorkspace
    header={
      <EnterpriseHeader
        title="Talent Pipeline"
        subtitle="Recruiting, registration, onboarding, document review, and approval."
        action={
         <button onClick={() => onInviteUser?.()}>
  Invite Worker
</button>
        }
      />
    }
  >



<details className="ao-secondary"><summary>Onboarding priorities</summary><OperationsCoach
  title="ShiftBridge Assist"
  subtitle="Today's operational priorities"
  estimatedMinutes={Math.max(
    5,
    (counts.registered || 0) * 3 +
      (counts.review_queue || 0) * 6 +
      (counts.onboarding || 0) * 2
  )}
  items={[
    ...(counts.registered > 0
      ? [{
          label: `🔴 Approve ${counts.registered} registration(s)`,
          bg: "#fee2e2",
          border: "#fecaca",
          color: "#991b1b",
          onClick: () => onNavigate?.("approvals"),
        }]
      : []),

    ...(counts.review_queue > 0
      ? [{
          label: `🟠 Review ${counts.review_queue} document package(s)`,
          bg: "#ffedd5",
          border: "#fdba74",
          color: "#9a3412",
          onClick: () => onNavigate?.("compliance"),
        }]
      : []),

    ...(counts.onboarding > 0
      ? [{
          label: `🟡 Follow up with ${counts.onboarding} onboarding worker(s)`,
          bg: "#fef3c7",
          border: "#fde68a",
          color: "#92400e",
          onClick: () => onNavigate?.("talentPipeline"),
        }]
      : []),

    ...(approvedWorkers.length > 0
      ? [{
          label: `🟢 ${approvedWorkers.length} worker(s) ready for scheduling`,
          bg: "#dcfce7",
          border: "#86efac",
          color: "#166534",
          onClick: () => onNavigate?.("workforce"),
        }]
      : []),
  ]}
/></details>

<ReviewQueue
  workers={reviewQueueWorkers}
  onReviewWorker={onReviewWorker}
/>



<ApplicantTable
  pipelineSearch={pipelineSearch}
  setPipelineSearch={setPipelineSearch}
  pipelineRoleFilter={pipelineRoleFilter}
  setPipelineRoleFilter={setPipelineRoleFilter}
  pipelineFacilityFilter={pipelineFacilityFilter}
  setPipelineFacilityFilter={setPipelineFacilityFilter}
  pipelineSourceFilter={pipelineSourceFilter}
  setPipelineSourceFilter={setPipelineSourceFilter}
  pipelineStageFilter={pipelineStageFilter}
  setPipelineStageFilter={setPipelineStageFilter}
>
<div style={{ display: "grid", gap: 12 }}>
  {groupedPipelineRows.map((stage) => {
    const isOpen = openStages[stage.key] !== false;

    return (
      <div
        key={stage.key}
        style={{
          border: `1px solid ${stage.border}`,
          borderRadius: 14,
          overflow: "hidden",
          background: "#ffffff",
        }}
      >
        <button
          type="button"
          onClick={() => toggleStage(stage.key)}
          style={{
            width: "100%",
            border: "none",
            background: stage.background,
            color: stage.color,
            padding: "12px 14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          <div style={{ fontWeight: 950, fontSize: 14 }}>
            {stage.icon} {stage.label}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontWeight: 950,
            }}
          >
            <span
              style={{
                minWidth: 28,
                textAlign: "center",
                padding: "3px 8px",
                borderRadius: 999,
                background: "#ffffff",
                border: `1px solid ${stage.border}`,
              }}
            >
              {stage.workers.length}
            </span>

            <span>{isOpen ? "−" : "+"}</span>
          </div>
        </button>

        {isOpen ? (
          stage.workers.length === 0 ? (
            <div
              style={{
                padding: 14,
                color: "#64748b",
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              No workers in this stage.
            </div>
          ) : (
            <div style={{ padding: "0 14px 8px" }}>
              <div className="ao-table-head"
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "2fr 1fr 1fr 1.4fr 1fr 1fr 1fr",
                  gap: 10,
                  padding: "10px 0",
                  borderBottom: "1px solid #e5e7eb",
                  fontWeight: 900,
                  color: "#475569",
                  fontSize: 12,
                }}
              >
                <div>Name / Email</div>
                <div>Role</div>
                <div>ZIP</div>
                <div>Exp / Radius</div>
                <div>Match</div>
                <div>Stage</div>
                <div>Action</div>
              </div>

                {stage.workers.map((u) => (
                  <ApplicantRow
                    key={u.uid || u.id || u.email}
                    worker={u}
                    onReviewWorker={onReviewWorker}
                    onViewWorker={onViewWorker}
                    onApproveRegistration={onApproveRegistration}
                    onOpenProfile={setSelectedApplicant}
                  />
                ))}

            </div>
          )
        ) : null}
      </div>
    );
  })}
</div>

</ApplicantTable>
<ReadyWorkforce
  approvedWorkers={approvedWorkers}
  facilities={pipelineFacilities}
  onViewWorker={onViewWorker}
  onFacilityAssigned={() => {
    // Optional: refresh workers here later
  }}
/>

<Applicant360Panel
  profile={profile}
  applicant={selectedApplicant}
  open={Boolean(selectedApplicant)}
  onClose={() => setSelectedApplicant(null)}
  onSendReminder={onSendReminder}
  onOpenWorker={(worker) => {
    onViewWorker?.(worker);
    setSelectedApplicant(null);
  }}
  onReviewDocuments={(worker) => {
    onReviewWorker?.(worker);
    setSelectedApplicant(null);
  }}
  onApproveApplicant={(worker) => {
    onApproveRegistration?.(worker);
    setSelectedApplicant(null);
  }}
/>

</OnboardingReviewWorkspace>
  );
}